import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { navMenus } from '../components/MainNav';
import { useAuth } from '../context/AuthContext';
import { filterMenuItems } from '../utils/menuAccess';
import { fetchCompanyDashboard } from '../api/dashboard';
import { fetchWinningNumberSummary } from '../api/winningNumbers';
import { todayISO } from '../utils/date';
import { fmtWin4 } from '../utils/lottery';
import './DashboardPage.css';

const menuItems = [
  { label: 'Central',    icon: '🌐', path: '/central' },
  { label: 'Rapports',   icon: '📋', path: '/rapports' },
  { label: 'Controles',  icon: '🔧', path: '/controles' },
  { label: 'Parametres', icon: '⚙️', path: '/parametres' },
  { label: 'Autres',     icon: '🛠️', path: null },
  { label: 'Bonos',      icon: '👤', path: '/controles/marriage-gratis' },
];

const money = (n) => (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const int = (n) => (n || 0).toLocaleString('en-US');

const Stat = ({ label, value, tone }) => (
  <div className={`dstat${tone ? ` dstat-${tone}` : ''}`}>
    <span className="dstat-label">{label}</span>
    <span className="dstat-value">{value}</span>
  </div>
);

const DashboardPage = () => {
  const navigate = useNavigate();
  const { company } = useAuth();
  const [date] = useState(todayISO());
  const [ov, setOv] = useState(null);
  const [wn, setWn] = useState({ rows: [], grand_total: 0, grand_tickets: 0 });

  // Restriction staff limite (cf. MainNav.jsx) : une tuile de section (Central,
  // Rapports, ...) ne doit rester visible que si au moins un item de cette
  // section lui est accorde — sinon elle menait vers une page-grille deja
  // filtree a vide, visible mais inutilisable (le bug signale par l'utilisateur).
  const menuKeys = company?.menu_keys;
  const visibleMenuItems = useMemo(() => {
    if (!Array.isArray(menuKeys)) return menuItems;
    return menuItems.filter((item) => {
      const sectionItems = navMenus[item.label];
      if (!sectionItems) return false;
      return filterMenuItems(sectionItems, menuKeys).length > 0;
    });
  }, [menuKeys]);

  useEffect(() => {
    let cancelled = false;
    fetchCompanyDashboard({ date }).then((d) => { if (!cancelled) setOv(d); }).catch(() => {});
    fetchWinningNumberSummary({ date }).then((d) => { if (!cancelled) setWn(d); }).catch(() => {});
    return () => { cancelled = true; };
  }, [date]);

  const m = ov?.money;
  const ex = ov?.exposure;

  return (
    <Layout showExtras>
      <p className="breadcrumb">Ruta: <Link to="/dashboard" className="breadcrumb-link">Menu</Link></p>

      <div className="icon-grid">
        {visibleMenuItems.map((item) => {
          const enabled = Boolean(item.path);
          return (
            <div
              key={item.label}
              className={`icon-item${enabled ? '' : ' icon-item-disabled'}`}
              onClick={enabled ? () => navigate(item.path) : undefined}
            >
              <div className="icon-box">{item.icon}</div>
              <span className="icon-label">{item.label}</span>
            </div>
          );
        })}
      </div>

      {ov && (
        <div className="dash-overview">
          <h3 className="dash-h">Aujourd'hui — {ov.date}</h3>
          <div className="dstat-grid">
            <Stat label="Total vendu" value={money(m.sales)} />
            <Stat label="Payout (gagné)" value={money(m.payout)} />
            <Stat label="Commission" value={money(m.commission)} />
            <Stat label="Profit / Marge" value={money(m.profit)} tone={m.profit < 0 ? 'warn' : 'ok'} />
            <Stat label="Fiches" value={int(m.tickets)} />
            <Stat label="Fiches gagnantes" value={int(m.winning_tickets)} />
          </div>

          <h3 className="dash-h">Structure</h3>
          <div className="dstat-grid">
            <Stat label="Groupes" value={int(ov.groups.total)} />
            <Stat label="Superviseurs" value={int(ov.supervisors.total)} />
            <Stat label="POS / Agents" value={int(ov.pos.total)} />
            <Stat label="POS en ligne" value={int(ov.pos.online)} tone="ok" />
            <Stat label="POS hors ligne" value={int(ov.pos.offline)} tone={ov.pos.offline ? 'warn' : null} />
            <Stat
              label="Exposition (pire couverture)"
              value={ex.worst ? `${ex.worst.covered_pct}%` : '—'}
              tone={ex.over_limit ? 'warn' : null}
            />
          </div>

          <h3 className="dash-h">Lots gagnants du jour</h3>
          <table className="dash-wn-table">
            <thead>
              <tr>
                <th>Lot</th><th>Tirage</th><th>Numéro</th><th>Win4</th>
                <th>Statut</th><th>Fiches gagnées</th><th>Somme totale</th>
              </tr>
            </thead>
            <tbody>
              {wn.rows.length === 0 && (
                <tr><td colSpan="7" className="dash-wn-empty">Aucun lot gagnant saisi aujourd'hui.</td></tr>
              )}
              {wn.rows.map((r) => (
                <tr key={r.tirage_code}>
                  <td>{r.tirage_code}</td>
                  <td>{r.tirage_name}</td>
                  <td className="dash-wn-num">{r.numbers}</td>
                  <td className="dash-wn-num">{fmtWin4(r.win4)}</td>
                  <td>{r.status_label}</td>
                  <td className="dash-wn-center">{r.winning_tickets}</td>
                  <td className="dash-wn-money">{money(r.total_gain)}</td>
                </tr>
              ))}
            </tbody>
            {wn.rows.length > 0 && (
              <tfoot>
                <tr className="dash-wn-total">
                  <td colSpan="5">Total</td>
                  <td className="dash-wn-center">{int(wn.grand_tickets)}</td>
                  <td className="dash-wn-money">{money(wn.grand_total)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </Layout>
  );
};

export default DashboardPage;
